import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import {
    FullImageElement,
    IconElement,
    ImageElement,
    MetricElement,
    ProductService,
    SuccessStoryElement
} from '../../../shared/services/catalog.service';
import {
    FileUploaderComponent,
    HcModal,
    HcSort,
    HcToastOptions,
    HcToasterService,
    ModalOptions,
    ModalService,
    PaginationComponent,
    SelectChangeEvent,
    SimpleModalOptions,
    TabChangeEvent
} from '@healthcatalyst/cashmere';
import { ChangeElement, ChangeService } from '../../../shared/services/change.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { DocumentService } from '../../../shared/services/document.service';
import { LightboxComponent } from '../../lightbox/lightbox.component';
import { ComponentCanDeactivate } from '../../can-deactivate/component-can-deactivate';
import { OktaService } from '../../../shared/services/okta.service';
import { TechnologyElement, TechnologyService } from '../../../shared/services/technology.service';
import { TypeElement, TypeService } from '../../../shared/services/type.service';
import { ProductLineElement, ProductLineService } from '../../../shared/services/product-line.service';
import { StatusElement, StatusService } from '../../../shared/services/status.service';
import { SLAElement, SLAService } from '../../../shared/services/sla.service';
import { ItemService } from '../../../shared/services/item.service';
import { AdminChangeLogComponent } from '../../individual-change-log/admin-change-log.component';
import { environment } from '../../../../environments/environment';
import { solutionCapabilities } from '../../../shared/solutionCapabilities';

/** Admin product element editor */
@Component({
    selector: 'pd-admin-technology-editor',
    templateUrl: './admin-technology-editor.component.html',
    styleUrls: ['./admin-technology-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminTechnologyEditorComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    modalRef: HcModal<TemplateRef<any>>;
    selectedTab = 0;
    technology: TechnologyElement;
    technologyLoading = true;
    technologyError = false;
    successStoriesEdited = false;
    imagesEdited = false;
    technologyID: number;
    itemID: number;
    savedItem = -1;
    revisionDate = '';
    solutionCapabilitiesList = solutionCapabilities;
    technologyForm = new FormGroup({
        nameControl: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        previousNamesControl: new FormArray([new FormControl('', { nonNullable: true })]),
        IDControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        typeControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        productLineControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        statusControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        slaControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        published: new FormControl(false, { nonNullable: true }),
        taglineControl: new FormControl('', { nonNullable: true }),
        problemControl: new FormControl('', { nonNullable: true }),
        approachControl: new FormControl('', { nonNullable: true }),
        benefitsFeaturesControl: new FormControl('', { nonNullable: true }),
        iconSVGControl: new FormControl('', { nonNullable: true }),
        iconPNGControl: new FormControl('', { nonNullable: true }),
        iconBase64Control: new FormControl('', { nonNullable: true }),
        caseStudiesControl: new FormControl('', { nonNullable: true }),
        techDescControl: new FormControl('', { nonNullable: true }),
        docsUrlControl: new FormControl('', { nonNullable: true }),
        communityUrlControl: new FormControl('', { nonNullable: true }),
        shortDescription: new FormControl('', { nonNullable: true }),
        subtitle: new FormControl('', { nonNullable: true }),
        solutionArea: new FormControl(1, { nonNullable: true }),
        solutionCapability: new FormControl('', { nonNullable: true })
    });

    successStudiesControl = new FormGroup({
        id: new FormControl(0, { nonNullable: true }),
        title: new FormControl('', { nonNullable: true }),
        url: new FormControl('', { nonNullable: true }),
        metrics: new FormArray([new FormControl('', { nonNullable: true })])
    });

    selectedImageFiles = new FormControl();
    imageForm = new FormGroup({});
    imgID = 0;
    images: FullImageElement[] = [];
    imagesToDelete: string[] = [];
    quillConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean'], ['link']]
    };
    quillCSConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], ['clean'], ['link']]
    };
    quillStyles = {
        'font-family': "'Noto Sans', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    };
    successStories: SuccessStoryElement[] = [];
    types: TypeElement[] = [];
    productLines: ProductLineElement[] = [];
    statuses: StatusElement[] = [];
    SLAs: SLAElement[] = [];

    saving = false;

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    @ViewChild('fileUploaderControl')
    fileUploaderControl: FileUploaderComponent;

    @ViewChild('changeLog')
    changeLog: AdminChangeLogComponent;

    get loading(): boolean {
        return this.technologyLoading;
    }

    get loadError(): boolean {
        return this.technologyError;
    }

    get technologyInputsDirty(): boolean {
        return this.technologyForm.dirty || this.imageForm.dirty || this.successStoriesEdited || this.imagesEdited;
    }

    constructor(
        private route: ActivatedRoute,
        private productService: ProductService,
        private technologyService: TechnologyService,
        private changeService: ChangeService,
        private modalService: ModalService,
        private toasterService: HcToasterService,
        private documentService: DocumentService,
        private oktaService: OktaService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private SLAService: SLAService,
        private itemService: ItemService
    ) {
        super();
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            // If a technology ID is supplied, we're editing an existing technology
            if (params['id']) {
                this.technologyID = params['id'];
            } else {
                // Otherwise we're creating a new technology so generate a new item ID for the technology
                this.itemService.getNewID().pipe(takeUntil(this.unsubscribe)).subscribe({
                    next: data => {                            
                        this.itemID = data['itemNumber'];
                        this.technologyForm.controls['IDControl'].setValue(data['itemNumber']);
                        this.technologyError = false;
                        this.technologyLoading = false;
                    },
                    error: msg => { 
                        console.log( msg );
                        this.technologyError = true;
                        this.technologyLoading = false;
                    }
                });
            }
        });

        this.typeService.catTypesUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.types = this.sortAlphabetically(list);
                if (this.productLines.length > 0 && this.statuses.length > 0 && this.SLAs.length > 0) {
                    this.technologyID && this.loadTechnology();
                }
            } else {
                !this.typeService.loadFailed ? this.typeService.loadCatTypes('technology') : this.technologyError = true;
            }
        });

        this.productLineService.productLineListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLines = this.sortAlphabetically(list);
                if (this.types.length > 0 && this.statuses.length > 0 && this.SLAs.length > 0) {
                    this.technologyID && this.loadTechnology();
                }
            } else {
                !this.productLineService.loadFailed ? this.productLineService.refresh() : this.technologyError = true;
            }
        });


        this.statusService.statusListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statuses = this.sortAlphabetically(list);
                if (this.types.length > 0 && this.productLines.length > 0 && this.SLAs.length > 0) {
                    this.technologyID && this.loadTechnology();
                }
            } else {
                !this.statusService.loadFailed ? this.statusService.refresh() : this.technologyError = true;
            }
        });

        this.SLAService.slaListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.SLAs = this.sortAlphabetically(list);
                for (let i = 0; i < this.SLAs.length; i++) {
                    if (this.SLAs[i].name === 'Health Catalyst') {
                        this.technologyForm.controls['slaControl'].setValue(this.SLAs[i].slaid);
                        break;
                    }
                }
                if (this.types.length > 0 && this.productLines.length > 0 && this.statuses.length > 0) {
                    this.technologyID && this.loadTechnology();
                }
            } else {
                !this.SLAService.loadFailed ? this.SLAService.refresh() : this.technologyError = true;
            }
        });
    }


    //alphabetical sort helper function
    sortAlphabetically(list: any[]) {
        return list.sort((a, b) =>
            a.name.localeCompare(b.name)
        )
    }

    //check if the Item ID is already in use and show an error on the form field if it is
    checkID( uploadModal?: TemplateRef<any> ): void {
        const currentID = this.technologyForm.controls['IDControl'].value!;

        if ( this.savedItem !== currentID ) {
            this.itemService.checkID(currentID).pipe(takeUntil(this.unsubscribe)).subscribe({
                next: data => {    
                    if ( data['itemExists'] ) {
                        this.technologyForm.controls['IDControl'].setErrors({ valid: false });
                    } else if( uploadModal ) {
                        this.onClickSave(uploadModal);
                    }
                },
                error: msg => { 
                    console.log( msg );
                }
            });
        } else if ( uploadModal ) {
            this.onClickSave(uploadModal);
        }
    }

    loadImages(imageArray: ImageElement[]) {
        let productImages: FullImageElement[] = [];
        imageArray.forEach((img: ImageElement) => {
            const placementVal = img.placement ? img.placement : (img.visibleInSheet ? 'Body' : 'Hidden');
            productImages.push({
                id: this.imgID,
                uploadID: img.id,
                type: img.type,
                caption: img.caption,
                fileName: img.fileName,
                visibleInSheet: img.visibleInSheet || false,
                uploadState: 2,
                placement: placementVal
            });

            this.imageForm.addControl('caption' + this.imgID, new FormControl(img.caption.length > 0 ? img.caption : ''));
            this.imageForm.addControl('placement' + this.imgID, new FormControl(placementVal));
            this.imgID++;
        });
        this.images = productImages;
    }

    checkPlacement( event: SelectChangeEvent, index: number ) {
        const placement = event.value;
        if ( placement !== 'Hidden' ) {
            for (let i = 0; i < this.images.length; i++) {
                if ( this.imageForm.get('placement' + this.images[i].id)!.value === placement && i !== index ) {
                    this.imageForm.get('placement' + this.images[i].id)!.setValue('Hidden');
                }
            }
        }
    }

    loadTechnology(): void {
        this.technologyService
            .detail(this.technologyID)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: data => {
                    this.revisionDate = data[0].revisionDate;
                    this.itemID = data[0].item;
                    this.savedItem = data[0].item;

                    //parse previous names, success stories, images, and icons strings from database
                    if (data[0].previousNames) {
                        data[0].previousNames = JSON.parse(data[0].previousNames);
                        //create form fields and patch previous names

                        //if the number of controls don't match the number of previous names, set up the controls
                        if (data[0].previousNames.length !== this.technologyForm.controls.previousNamesControl.controls.length || data[0].previousNames.length === 1) {
                            for (let i = 0; i < data[0].previousNames.length; i++) {
                                if (i === 0) {
                                    this.technologyForm.controls.previousNamesControl.controls[i].patchValue(data[0].previousNames[0]);
                                } else {
                                    this.technologyForm.controls.previousNamesControl.push((new FormControl(data[0].previousNames[i], { nonNullable: true })));
                                }
                            }
                        }
                    }

                    if (data[0].successStories) {
                        data[0].successStories = JSON.parse(data[0].successStories);
                    }

                    if (data[0].images) {
                        const imgData = JSON.parse(data[0].images);
                        this.loadImages(imgData);
                    }

                    if (data[0].icons) {
                        data[0].icons = JSON.parse(data[0].icons);
                    }

                    //if there are success stories, assign a copy of the array to the local success stories variable
                    data[0].successStories && (this.successStories = data[0].successStories.slice());

                    //patch for fields with technology data
                    this.technologyForm.patchValue({
                        nameControl: data[0].name,
                        IDControl: data[0].item,
                        typeControl: data[0].typeID,
                        productLineControl: data[0].productLineID,
                        statusControl: data[0].statusID,
                        slaControl: data[0].slaid,
                        published: data[0].published,
                        taglineControl: data[0].tagline,
                        problemControl: data[0].problem,
                        approachControl: data[0].approach,
                        benefitsFeaturesControl: data[0].features,
                        iconSVGControl: data[0].icons['svgURL'],
                        iconPNGControl: data[0].icons['pngURL'],
                        iconBase64Control: data[0].icons['base64URL'],
                        caseStudiesControl: data[0].caseStudies,
                        techDescControl: data[0].techDescription,
                        docsUrlControl: data[0].docsURL,
                        communityUrlControl: data[0].communityURL,
                        shortDescription: data[0].shortDescription,
                        subtitle: data[0].subtitle,
                        solutionArea: data[0].solutionArea ? data[0].solutionArea : 1,
                        solutionCapability: data[0].solutionCapability ? data[0].solutionCapability : this.solutionCapabilitiesList[(data[0].solutionArea ? data[0].solutionArea : 1)-1][0].id                    
                    });

                    this.technology = {
                        technologyID: data[0].technologyID,
                        name: data[0].name,
                        item: data[0].item,
                        typeID: data[0].typeID,
                        productLineID: data[0].productLineID,
                        statusID: data[0].statusID,
                        slaid: data[0].slaid,
                        published: data[0].published,
                        revisionDate: data[0].revisionDate,
                        tagline: data[0].tagline,
                        problem: data[0].problem,
                        approach: data[0].approach,
                        features: data[0].features,
                        images: data[0].images,
                        icons: data[0].icons,
                        caseStudies: data[0].caseStudies,
                        techDescription: data[0].techDescription,
                        docsURL: data[0].docsURL,
                        communityURL: data[0].communityURL,
                        successStories: data[0].successStories,
                        previousNames: data[0].previousNames,
                        analyticsAccelerator: data[0].analyticsAccelerator,
                        shortDescription: data[0].shortDescription,
                        subtitle: data[0].subtitle,
                        solutionArea: data[0].solutionArea,
                        solutionCapability: data[0].solutionCapability
                    };

                    this.changeLog.loadChanges(this.itemID, "technology");

                    this.technologyForm.markAsPristine();
                    this.imageForm.markAsPristine();
                    this.technologyError = false;
                    this.technologyLoading = false;
                },
                error: msg => {
                    this.technologyError = true;
                    this.technologyLoading = false;
                    console.log(msg);
                }
            });
    }

    ////Previous Name Functions////
    addPrevName() {
        this.technologyForm.controls.previousNamesControl.push(new FormControl('', { nonNullable: true }));
        this.technologyForm.controls.previousNamesControl.markAsDirty();
    }

    removePrevName(index: number) {
        this.technologyForm.controls.previousNamesControl.removeAt(index);
        this.technologyForm.controls.previousNamesControl.markAsDirty();
    }
    ////End Previous Name Functions////

    ////Image Functions////
    //add images and corresponding form controls to images table
    addImages() {
        //check that all files are images
        let fileCheckFail = false;
        for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
            if (!this.selectedImageFiles.value[i].type.includes('image/')) {
                fileCheckFail = true;
                break;
            }
        }

        if (fileCheckFail) {
            this.selectedImageFiles.markAsTouched();
            this.selectedImageFiles.setErrors({ invalid: true });
        } else {
            for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
                // add form controls and update the array of files to save
                this.imageForm.addControl('caption' + this.imgID, new FormControl(''));
                this.imageForm.addControl('placement' + this.imgID, new FormControl('Hidden'));
                this.images.push({
                    id: this.imgID,
                    uploadID: '',
                    caption: '',
                    type: this.selectedImageFiles.value[i].type,
                    fileName: this.selectedImageFiles.value[i].name,
                    visibleInSheet: false,
                    data: this.selectedImageFiles.value[i],
                    uploadState: 0,
                    placement: 'Hidden'
                });
                this.imgID++;
            }

            this.imagesEdited = true;

            //clear selected images
            if (this.fileUploaderControl) {
                setTimeout(() => {
                    this.fileUploaderControl.reset();
                });
            }
            this.selectedImageFiles = new FormControl();
        }
    }

    //open confirm removal modal, if the user confirms then remove the image reference
    confirmRemoveImage(img: FullImageElement) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the image: <strong>' + img.fileName + '</strong>?<p>',
            headerText: 'Remove Image',
            confirmButtonText: 'Remove Image',
            icon: ''
        };
        this.modalService
            .confirmDestructive(options)
            .pipe(
                take(1),
                filter(confirmed => confirmed)
            )
            .subscribe(() => {
                this.removeImageRef(img);
            });
    }

    //remove the image from the list of images and add it to the array of images to delete if it was already uploaded
    removeImageRef(img: FullImageElement) {
        let imgIndex = -1;
        for (let i = 0; i < this.images.length; i++) {
            if (img.id === this.images[i].id) {
                imgIndex = i;
                break;
            }
        }
        if (imgIndex > -1) {
            this.images.splice(imgIndex, 1);
            if (img.uploadID) {
                this.imagesToDelete.push(img.uploadID);
            }
            this.imagesEdited = true;
        }
    }

    //upload images, one by one
    uploadNextImage(): void {
        // Check if there are any images left to upload
        let nextIndex = -1;
        for (let i = this.images.length - 1; i >= 0; i--) {
            if (!this.images[i].uploadID && this.images[i].uploadState !== -1 && this.images[i].uploadState !== 2) {
                nextIndex = i;
            }
        }

        if (nextIndex !== -1) {
            if (this.images[nextIndex].data) {
                this.images[nextIndex].uploadState = 1;
                this.documentService
                    .addImage(this.images[nextIndex].data!)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        data => {
                            this.images[nextIndex].uploadState = 2;
                            this.images[nextIndex].uploadID = data.location;
                            this.uploadNextImage();
                        },
                        error => {
                            this.images[nextIndex].uploadState = -1;
                            console.log(error);
                            this.uploadNextImage();
                        }
                    );
            } else {
                this.images[nextIndex].uploadState = -1;
                this.uploadNextImage();
            }
        }
    }

    //delete images from blob storage
    deleteImages(): void {
        if (this.imagesToDelete.length > 0) {
            this.imagesToDelete.forEach((element, index) => {
                this.documentService
                    .removeImage(element)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        () => { },
                        error => {
                            console.log(error);
                        }
                    );
            });
        }
    }
    ////End Image Functions////

    ////Success Story Functions////
    //open add/edit success story modal
    openAddEditModal(content: TemplateRef<any>, edit: boolean, successStory?: SuccessStoryElement) {
        const options: ModalOptions = {
            size: 'md',
            data: {
                successStory: successStory,
                metricNum: successStory?.metrics.length,
                edit: edit
            }
        };

        //reset metrics control count
        this.successStudiesControl.controls.metrics = new FormArray([new FormControl('', { nonNullable: true })]);

        //reset all case study control values
        this.successStudiesControl.reset();

        //if there is case study data, populate it in the fields
        if (successStory) {
            if (successStory.metrics.length > 0) {
                successStory.metrics.forEach((metric, index) => {
                    if (index > 0) {
                        this.successStudiesControl.controls.metrics.push(new FormControl());
                    }
                    this.successStudiesControl.controls.metrics.controls[index].patchValue(metric.name);
                });
            }
            this.successStudiesControl.patchValue({
                id: successStory.id,
                title: successStory.title,
                url: successStory.url
            });
        }

        this.modalRef = this.modalService.open(content, options);
    }

    editSuccessStory() {
        let metrics: MetricElement[] = [];

        if (this.successStudiesControl.controls.metrics.length > 0) {
            this.successStudiesControl.controls.metrics.controls.forEach((metric, index) => {
                if (metric.value) {
                    metrics.push({
                        id: index,
                        name: metric.value
                    });
                }
            });
        }

        const successStoryToEdit: SuccessStoryElement = {
            id: this.successStudiesControl.controls['id'].value,
            title: this.successStudiesControl.controls['title'].value,
            url: this.successStudiesControl.controls['url'].value,
            metrics: metrics
        };

        this.successStories[this.successStudiesControl.controls['id'].value] = successStoryToEdit;
        this.successStoriesEdited = true;
        this.modalRef.close();
    }

    addSuccessStory() {
        const successStoryId = this.successStories?.length;
        let metrics: MetricElement[] = [];

        if (this.successStudiesControl.controls.metrics.length > 0) {
            this.successStudiesControl.controls.metrics.controls.forEach((metric, index) => {
                if (metric.value) {
                    metrics.push({
                        id: index,
                        name: metric.value
                    });
                }
            });
        }

        const successStoryToAdd: SuccessStoryElement = {
            id: successStoryId || 0,
            title: this.successStudiesControl.controls['title'].value,
            url: this.successStudiesControl.controls['url'].value,
            metrics: metrics
        };

        this.successStories?.push(successStoryToAdd);
        this.successStoriesEdited = true;
        this.modalRef.close();
    }

    //open confirm removal modal, if the user confirms then remove the case study
    confirmSuccessStoryRemove(successStory: SuccessStoryElement) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the success story: <strong>' + successStory.title + '</strong>?<p>',
            headerText: 'Remove Success Story',
            confirmButtonText: 'Remove Success Story',
            icon: ''
        };
        this.modalService
            .confirmDestructive(options)
            .pipe(
                take(1),
                filter(confirmed => confirmed)
            )
            .subscribe(() => {
                this.removeSuccessStory(successStory.id);
            });
    }

    removeSuccessStory(successStoryId: number) {
        for (let i = 0; i < this.successStories.length; i++) {
            if (this.successStories[i].id === successStoryId) {
                this.successStories.splice(i, 1);
                this.successStoriesEdited = true;
                break;
            }
        }
    }

    addMetric() {
        this.successStudiesControl.controls.metrics.push(new FormControl());
        this.successStudiesControl.markAsDirty();
    }

    removeMetric(index: number) {
        this.successStudiesControl.controls['metrics'].removeAt(index);
        this.successStudiesControl.markAsDirty();
    }


    //when a user clicks save, delete images and upload unsaved images OR save the technology
    onClickSave(imgUploadModal: TemplateRef<any>) {
        this.saving = true;
        const options: HcToastOptions = {
            header: 'Save Started',
            body: 'Your changes are being saved and synced. This may take several seconds.',
            type: 'info',
            position: 'bottom-right'
        };
        this.toasterService.addToast(options);

        this.deleteImages();

        if (this.images.length > 0) {
            //make list of images that are not already saved
            let unsavedImages: FullImageElement[] = [];
            this.images.forEach(element => {
                if (element.uploadState != 2) {
                    unsavedImages.push(element);
                }
            });

            //if there are unsaved images, save them
            if (unsavedImages.length > 0) {
                const options: ModalOptions = {
                    size: 'lg',
                    ignoreOverlayClick: true,
                    ignoreEscapeKey: true,
                    data: { images: unsavedImages }
                };

                //open upload progress modal
                this.modalRef = this.modalService.open(imgUploadModal, options);
                this.uploadNextImage();
            } else {
                this.saveTechnology();
            }
        } else {
            this.saveTechnology();
        }
    }

    //// Check for changes in all technology attributes and log them ////
    logChanges(technology: TechnologyElement) {
        let changes: ChangeElement[] = [];
        const date = new Date().toISOString();

        //check if name changed
        if (this.technology.name !== technology.name) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Name',
                previous: this.technology.name,
                update: technology.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to previous names////
        //check for changes in previous names values
        
        const prevNamesObj: string[] = typeof this.technology.previousNames === 'object' ? this.technology.previousNames : JSON.parse(this.technology.previousNames);
        const prevStr = JSON.stringify(prevNamesObj);
        const updateStr = JSON.stringify(technology.previousNames);

        //if there's an previous name change, add it
        if (prevStr !== updateStr) {
            const prevNameChange = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Previous Names',
                previous: prevStr,
                update: updateStr,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(prevNameChange);
        }
        ////End checks for changes to previous names////

        //check if item changed
        if (this.technology.item !== technology.item) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Item ID',
                previous: this.technology.item.toString(),
                update: technology.item.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }


        //check if type changed

        //translate typeID into type name??
        if (this.technology.typeID !== technology.typeID) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Type',
                previous: this.technology.typeID.toString(),
                update: technology.typeID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if product line changed

        //translate productLineID into product line name??
        if (this.technology.productLineID !== technology.productLineID) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Product Line',
                previous: this.technology.productLineID.toString(),
                update: technology.productLineID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if status changed

        //translate statusID into status name??
        if (this.technology.statusID !== technology.statusID) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Status',
                previous: this.technology.statusID.toString(),
                update: technology.statusID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if sla changed

        //translate slaID into SLA name??
        if (this.technology.slaid !== technology.slaid) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'SLA',
                previous: this.technology.slaid.toString(),
                update: technology.slaid.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if published status changed
        if (this.technology.published !== technology.published) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Published Status',
                previous: this.technology.published.toString(),
                update: technology.published.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }


        //check if tagline changed
        if (this.technology.tagline !== technology.tagline) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Tagline',
                previous: this.technology.tagline,
                update: technology.tagline,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if problem changed
        if (this.technology.problem !== technology.problem) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Problem',
                previous: this.technology.problem,
                update: technology.problem,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if approach changed
        if (this.technology.approach !== technology.approach) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Approach',
                previous: this.technology.approach,
                update: technology.approach,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if features changed
        if (this.technology.features !== technology.features) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Benefits and Features',
                previous: this.technology.features,
                update: technology.features,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if short description changed
        if (this.technology.shortDescription !== technology.shortDescription) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Short Description',
                previous: this.technology.shortDescription,
                update: technology.shortDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if subtitle changed
        if (this.technology.subtitle !== technology.subtitle) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Subtitle',
                previous: this.technology.subtitle,
                update: technology.subtitle,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if Solution Area changed
        if (this.technology.solutionArea !== technology.solutionArea) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Solution Area',
                previous: this.technology.solutionArea ? this.technology.solutionArea.toString() : 'none',
                update: technology.solutionArea.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if Solution Capability changed
        if (this.technology.solutionCapability !== technology.solutionCapability) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Solution Capability',
                previous: this.technology.solutionCapability ? this.solutionCapabilitiesList.flat().find(c => c.id === this.technology.solutionCapability)!.name : 'none',
                update: this.solutionCapabilitiesList.flat().find(c => c.id === technology.solutionCapability)!.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to images////
        //check for changes in image attributes
        let imagesChanged = false;

        const prevImgObj: ImageElement[] = typeof this.technology.images === 'object' ? this.technology.images : JSON.parse(this.technology.images);

        technology.images.forEach(element => {
            for (let i = 0; i < prevImgObj.length; i++) {
                if (element.id === prevImgObj[i].id) {
                    if (element.fileName !== prevImgObj[i].fileName) {
                        imagesChanged = true;
                    }

                    if (element.visibleInSheet !== prevImgObj[i].visibleInSheet) {
                        imagesChanged = true;
                    }

                    if (element.caption !== prevImgObj[i].caption) {
                        imagesChanged = true;
                    }

                    if (element.type !== prevImgObj[i].type) {
                        imagesChanged = true;
                    }

                    if (element.placement !== prevImgObj[i].placement) {
                        imagesChanged = true;
                    }
                }
            }
        });

        // check for change in amount of images
        if (technology.images.length !== prevImgObj.length) {
            imagesChanged = true;
        }

        //if there's an image change, add it
        if (imagesChanged) {
            const imageChange = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Images',
                previous: JSON.stringify(prevImgObj),
                update: JSON.stringify(technology.images),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(imageChange);
        }
        ////End checks for changes to images////

        ////Checks for changes to icons////
        //check for changes in icon attributes
        let iconsChanged = false;

        if (this.technology.icons.svgURL !== technology.icons.svgURL) {
            iconsChanged = true;
        } else if (this.technology.icons.pngURL !== technology.icons.pngURL) {
            iconsChanged = true;
        } else if (this.technology.icons.base64URL !== technology.icons.base64URL) {
            iconsChanged = true;
        }

        if (iconsChanged) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Icons',
                previous: JSON.stringify(this.technology.icons),
                update: JSON.stringify(technology.icons),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }
        ////End checks for changes to icons////

        ////Checks for changes to success stories////
        //check for changes in success story attributes
        let successStoriesChanged = false;
        technology.successStories.forEach(element => {
            for (let i = 0; i < this.technology.successStories.length; i++) {
                if (element.id === this.technology.successStories[i].id) {
                    if (element.title !== this.technology.successStories[i].title) {
                        successStoriesChanged = true;
                    }
                    if (element.url !== this.technology.successStories[i].url) {
                        successStoriesChanged = true;
                    }

                    if (element.metrics) {
                        for (let j = 0; j < element.metrics.length; j++) {
                            if (element.metrics[j].id === this.technology.successStories[i].metrics[j].id) {
                                if (element.metrics[j].name !== this.technology.successStories[i].metrics[j].name) {
                                    successStoriesChanged = true;
                                }
                            }
                        }
                    }
                }
            }
        });

        //check for a change in the amount of success stories
        if (technology.successStories.length !== this.technology.successStories.length) {
            successStoriesChanged = true;
        }

        //if there is a success stories change, add it
        if (successStoriesChanged) {
            const successStoryChange = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Success Stories',
                previous: JSON.stringify(this.technology.successStories),
                update: JSON.stringify(technology.successStories),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(successStoryChange);
        }
        ////End checks for changes to success stories////

        //check if case studies changed
        if (this.technology.caseStudies !== technology.caseStudies) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Case Studies',
                previous: this.technology.caseStudies,
                update: technology.caseStudies,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if techDescription changed
        if (this.technology.techDescription !== technology.techDescription) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Technical Description',
                previous: this.technology.techDescription,
                update: technology.techDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if docsURL changed
        if (this.technology.docsURL !== technology.docsURL) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Docs URL',
                previous: this.technology.docsURL,
                update: technology.docsURL,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if communityURL changed
        if (this.technology.communityURL !== technology.communityURL) {
            const change = {
                item: technology.item,
                name: technology.name,
                category: 'technology',
                field: 'Community URL',
                previous: this.technology.communityURL,
                update: technology.communityURL,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        if (changes.length > 0) {
            this.changeService.add(changes);
        }
    }

    saveTechnology() {
        //grab update date
        const date = new Date().toISOString();

        //turn full image element into image element
        let finalImages: ImageElement[] = [];
        this.images &&
            this.images.forEach(img => {
                const captionControl = 'caption' + img.id;
                const placementControl = 'placement' + img.id;

                if (img.uploadID) {
                    finalImages.push({
                        id: img.uploadID,
                        fileName: img.fileName,
                        visibleInSheet: img.placement !== 'Hidden',
                        type: img.type,
                        caption: this.imageForm.get(captionControl)!.value,
                        placement: this.imageForm.get(placementControl)!.value
                    });
                }
            });

        //turn icon URLs into object
        let iconURLS: IconElement = {
            'svgURL': this.technologyForm.controls['iconSVGControl'].value,
            'pngURL': this.technologyForm.controls['iconPNGControl'].value,
            'base64URL': this.technologyForm.controls['iconBase64Control'].value,
        }

        //turn previous names into object
        let previousNames: string[] = [];
        for (let i = 0; i < this.technologyForm.controls.previousNamesControl.length; i++) {
            previousNames.push(this.technologyForm.controls.previousNamesControl.controls[i].value);
        }

        // Find the Type ID for the Analytics Accelerator type if it exists
        let acceleratorTypeId = -1;
        for ( let i=0; i < this.types.length; i++ ) {
            if ( this.types[i].name === 'Analytics Accelerator' ) {
                acceleratorTypeId = this.types[i].typeID;
                break;
            }
        }

        //create the technology object to save
        const technology: TechnologyElement = {
            technologyID: this.technologyID || -1,
            name: this.technologyForm.controls['nameControl'].value,
            item: this.technologyForm.controls['IDControl'].value!,
            typeID: this.technologyForm.controls['typeControl'].value!,
            productLineID: this.technologyForm.controls['productLineControl'].value!,
            statusID: this.technologyForm.controls['statusControl'].value!,
            slaid: this.technologyForm.controls['slaControl'].value!,
            published: this.technologyForm.controls['published'].value,
            revisionDate: date,
            tagline: this.technologyForm.controls['taglineControl'].value,
            problem: this.technologyForm.controls['problemControl'].value,
            approach: this.technologyForm.controls['approachControl'].value,
            features: this.technologyForm.controls['benefitsFeaturesControl'].value,
            images: finalImages,
            icons: iconURLS,
            caseStudies: this.technologyForm.controls['caseStudiesControl'].value,
            techDescription: this.technologyForm.controls['techDescControl'].value,
            docsURL: this.technologyForm.controls['docsUrlControl'].value,
            communityURL: this.technologyForm.controls['communityUrlControl'].value,
            successStories: this.successStories.slice(),
            previousNames: previousNames,
            analyticsAccelerator: acceleratorTypeId === this.technologyForm.controls['typeControl'].value,
            shortDescription: this.technologyForm.controls['shortDescription'].value,
            subtitle: this.technologyForm.controls['subtitle'].value,
            solutionArea: this.technologyForm.controls['solutionArea'].value,
            solutionCapability: this.technologyForm.controls['solutionCapability'].value
        };

        //if the technology is being edited, update it, log changes, and load changes
        if (this.technologyID) {
            this.technologyService
                .update(technology)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: () => {
                        this.logChanges(technology);
                        this.technologyService.refresh();
                        this.productService.refresh();
                        this.changeLog.loadChanges(this.itemID, "technology");
                        const options: HcToastOptions = {
                            header: 'Technology Updated',
                            body: technology.name + ' successfully updated.',
                            type: 'success',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);

                        //mark everything pristine, update technology variable, and update revision date
                        this.technologyForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.successStoriesEdited = false;
                        this.imagesEdited = false;
                        this.technology = technology;
                        this.revisionDate = date;
                        this.saving = false;
                    },
                    error: msg => {
                        this.saving = false;
                        const options: HcToastOptions = {
                            header: 'Update Failed',
                            body: 'Unable to update technology. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        } else {
            //if the technology is being added, add it, log an add change, update the item ID, and load changes
            this.technologyService
                .add(technology)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: result => {

                        this.changeService.add([
                            {
                                item: technology.item,
                                name: technology.name,
                                category: 'technology',
                                field: '--',
                                previous: '--',
                                update: 'technology added',
                                user: this.oktaService.username.value,
                                date: new Date().toISOString()
                            }
                        ]);
                        this.technologyService.refresh();
                        this.technologyID = result;
                        this.itemID = technology.item;
                        this.savedItem = technology.item;
                        this.changeLog.loadChanges(this.itemID, "technology");
                        const options: HcToastOptions = {
                            header: 'Technology Added',
                            body: technology.name + ' successfully added.',
                            type: 'success',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);

                        //mark everything pristine and update technology variable
                        this.technologyForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.successStoriesEdited = false;
                        this.imagesEdited = false;
                        this.technology = technology;
                        this.revisionDate = date;
                        this.saving = false;
                    },
                    error: msg => {
                        this.saving = false;
                        const options: HcToastOptions = {
                            header: 'Add Failed',
                            body: 'Unable to add technology. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        }
    }

    openLightbox(img: FullImageElement) {
        const options: ModalOptions = {
            size: 'auto',
            closeIcon: true,
            data: {
                img
            }
        };

        this.modalService.open(LightboxComponent, options);
    }

    openPdf(): void {
        window.open('/viewer?id=' + this.itemID.toString(), '_blank');
    }

    generateLink() {
        const link = environment.root.url + '/viewer?id=' + this.itemID.toString();
        navigator.clipboard.writeText(link);

        const options: HcToastOptions = {
            header: 'Copied pdf URL for ' + this.technology.name,
            type: 'info',
            position: 'bottom-center'
        };
        this.toasterService.addToast(options);
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    tabClick(selected: TabChangeEvent) {
        //ensure that newest changes are loaded if a user just updated
        selected.index === 1 && this.changeLog.loadChanges(this.itemID, "technology");
        this.selectedTab = selected.index;
    }

    canDeactivate(): boolean {
        return !this.technologyInputsDirty;
    }

    defaultSolutionCapability(): void {
        this.technologyForm.controls['solutionCapability'].setValue( this.solutionCapabilitiesList[this.technologyForm.controls['solutionArea'].value - 1][0].id );
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
